/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Quick Schedule Review Step — content tree and assignment schedule builder.
 *
 * @file QuickScheduleReviewStep.jsx
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import React, {
  useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import AssignmentList from '../assignments/AssignmentList';
import { ChapterContentProvider } from '../chapter-content';
import ChapterContentTree from '../chapter-content/ChapterContentTree';
import {
  parseDragPayload,
  buildScheduleRestrictedTocData
} from '../chapter-content/chapterContentTreeUtils';
import { QuickScheduleReviewStepStylesRoot, classes } from './styles/QuickScheduleReviewStepStyles';
import AssignmentDueDateModal from '../assignments/AssignmentDueDateModal';
import {
  ASSISTED_SCHEDULE_TREE_MENU_CONFIG,
  applyAddAssignmentContentToDate,
  applyAssignmentTitleUpdate,
  applyContentDrop,
  applyClearAssignmentContent,
  applySectionRemove,
  applyRemoveAssignmentSlot,
  applyRescheduleAssignmentDate,
  buildQuickScheduleAssignments,
  createScheduleAddToDateFilter,
  createScheduleAddToDateValidator,
  getScheduleDateBounds,
  hasQuickScheduleSlotZeroQuestions,
  isScheduleTocAddToDateMenuEnabled,
  QUICK_SCHEDULE_ASSIGNMENT_LIST_ITEM_CONFIG
} from './quickScheduleReviewUtils';
import useScheduleDragDropTouch from './useScheduleDragDropTouch';

const ADD_TO_DATE_MODAL_ASSIGNMENT_ID = 'quick-schedule-add-to-date';

const noopToggleNode = () => {};

const cloneTocData = (tocData = []) => JSON.parse(JSON.stringify(tocData));

const getAssignmentIds = (assignments = []) => new Set((assignments || []).map((item) => item.id));

const QuickScheduleReviewStep = ({
  textInfo,
  scheduleFormValues,
  tocData: tocDataProp,
  chapterMcqMapping,
  assignments: assignmentsProp,
  editingAssignmentId: editingAssignmentIdProp,
  callback
}) => {
  const generatedAssignments = useMemo(
    () => buildQuickScheduleAssignments(scheduleFormValues),
    [scheduleFormValues]
  );

  const [sourceTocData, setSourceTocData] = useState(() => cloneTocData(tocDataProp || []));
  const [assignments, setAssignments] = useState(
    () => assignmentsProp || generatedAssignments
  );
  const [editingAssignmentId, setEditingAssignmentId] = useState(editingAssignmentIdProp || '');
  const [addToDateContext, setAddToDateContext] = useState(null);
  const [scrollToAssignmentId, setScrollToAssignmentId] = useState('');
  const reviewPaneRef = useRef(null);
  const previousAssignmentIdsRef = useRef(getAssignmentIds(assignments));
  useScheduleDragDropTouch(reviewPaneRef);

  /**
   * A single new card ("Add new" or "Add to new assignment" in the host app) gets
   * selected and scrolled into view. Bulk changes (step 1 regenerate) are ignored.
   */
  useEffect(() => {
    const previousIds = previousAssignmentIdsRef.current;
    const addedIds = (assignments || [])
      .map((item) => item.id)
      .filter((id) => !previousIds.has(id));

    previousAssignmentIdsRef.current = getAssignmentIds(assignments);

    if (previousIds.size && addedIds.length === 1) {
      setEditingAssignmentId(addedIds[0]);
      setScrollToAssignmentId(addedIds[0]);
    }
  }, [assignments]);

  useEffect(() => {
    if (!scrollToAssignmentId) {
      return;
    }

    const card = reviewPaneRef.current?.querySelector(
      `[data-schedule-slot-id="${scrollToAssignmentId}"]`
    );

    card?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    setScrollToAssignmentId('');
  }, [scrollToAssignmentId]);

  useEffect(() => {
    if (tocDataProp) {
      setSourceTocData(cloneTocData(tocDataProp));
    }
  }, [tocDataProp]);

  /** Rebuild slots when step 1 values change unless the parent controls assignments. */
  useEffect(() => {
    if (assignmentsProp) {
      setAssignments(assignmentsProp);

      return;
    }

    setAssignments(generatedAssignments);
    setEditingAssignmentId('');
  }, [assignmentsProp, generatedAssignments]);

  useEffect(() => {
    if (editingAssignmentIdProp !== undefined) {
      setEditingAssignmentId(editingAssignmentIdProp || '');
    }
  }, [editingAssignmentIdProp]);

  const tocData = useMemo(
    () => buildScheduleRestrictedTocData(sourceTocData, assignments),
    [assignments, sourceTocData]
  );

  /**
   * Display-only copy of the slots with an inline warning flag in
   * case they dont have any questions
   */
  const assignmentsWithWarnings = useMemo(() => (assignments || []).map((assignment) => ({
    ...assignment,
    hasNoQuestionsWarning: hasQuickScheduleSlotZeroQuestions(assignment, {
      questionCounts: chapterMcqMapping,
      sourceTocData
    })
  })), [assignments, chapterMcqMapping, sourceTocData]);

  const treeTextInfo = useMemo(() => ({
    ...textInfo,
    scheduleMenuConfig: textInfo.scheduleMenuConfig || ASSISTED_SCHEDULE_TREE_MENU_CONFIG
  }), [textInfo]);

  const scheduleTocAddToDateEnabled = useMemo(
    () => isScheduleTocAddToDateMenuEnabled(treeTextInfo.scheduleMenuConfig),
    [treeTextInfo.scheduleMenuConfig]
  );

  const closeAddToDateModal = useCallback(() => {
    setAddToDateContext(null);
    setEditingAssignmentId('');
  }, []);

  const updateAssignments = useCallback((nextAssignments) => {
    setAssignments(nextAssignments);
    callback?.onAssignmentsChange?.(nextAssignments);
  }, [callback]);

  const handleContentDrop = useCallback((assignment, rawPayload) => {
    const nodePayload = parseDragPayload(rawPayload);

    if (!nodePayload) {
      return;
    }

    const result = applyContentDrop({
      assignments,
      assignmentId: assignment.id,
      nodePayload,
      sourceTocData
    });

    updateAssignments(result.assignments);
    setEditingAssignmentId('');
    callback?.onContentDrop?.({
      assignmentId: assignment.id,
      nodePayload,
      assignments: result.assignments
    });
  }, [assignments, callback, sourceTocData, updateAssignments]);

  /** Toggle inline edit on the schedule card; title editing is enabled while selected. */
  const handleAssignmentEdit = useCallback((assignment) => {
    setEditingAssignmentId((previousId) => (
      previousId === assignment.id ? '' : assignment.id
    ));
    callback?.onAssignmentEdit?.(assignment);
  }, [callback]);

  const handleAssignmentDeselect = useCallback((assignment) => {
    setEditingAssignmentId('');
    callback?.onAssignmentCancelEdit?.(assignment);
  }, [callback]);

  const handleSectionRemove = useCallback((assignment, sectionNumber) => {
    const result = applySectionRemove({
      assignments,
      assignmentId: assignment.id,
      sectionNumber,
      sourceTocData
    });

    updateAssignments(result.assignments);
    callback?.onSectionRemove?.({
      assignmentId: assignment.id,
      sectionNumber,
      assignments: result.assignments
    });
  }, [assignments, callback, sourceTocData, updateAssignments]);

  const handleAssignmentClearData = useCallback((assignment) => {
    const result = applyClearAssignmentContent({
      assignments,
      assignmentId: assignment.id
    });

    updateAssignments(result.assignments);
    callback?.onAssignmentRemove?.({
      assignmentId: assignment.id,
      assignments: result.assignments
    });
  }, [assignments, callback, updateAssignments]);

  const handleAssignmentSlotRemove = useCallback((assignment) => {
    const hasContent = (assignment.contentNodes?.length || assignment.sections?.length || 0) > 0;

    if (hasContent) {
      handleAssignmentClearData(assignment);

      return;
    }

    const result = applyRemoveAssignmentSlot({
      assignments,
      assignmentId: assignment.id
    });

    updateAssignments(result.assignments);
    setEditingAssignmentId((previousId) => (
      previousId === assignment.id ? '' : previousId
    ));
    callback?.onAssignmentSlotRemove?.({
      assignmentId: assignment.id,
      assignments: result.assignments
    });
  }, [assignments, callback, handleAssignmentClearData, updateAssignments]);

  const handleAssignmentTitleChange = useCallback((assignment, title) => {
    const result = applyAssignmentTitleUpdate({
      assignments,
      assignmentId: assignment.id,
      title
    });

    updateAssignments(result.assignments);
    callback?.onAssignmentSave?.({
      assignmentId: assignment.id,
      title,
      assignments: result.assignments
    });
  }, [assignments, callback, updateAssignments]);

  const handleScheduleMenuAction = useCallback((menuItemId, node) => {
    callback?.onScheduleMenuAction?.(menuItemId, node);
  }, [callback]);

  /** Tree Add to assignment — host app opens QuickScheduleAddToAssignmentModal via callback. */
  const handleAddToDateFromTree = useCallback((node) => {
    if (!scheduleTocAddToDateEnabled) {
      return;
    }

    callback?.onOpenAddToAssignmentModal?.(node);
  }, [callback, scheduleTocAddToDateEnabled]);

  /**
   * Assignment card date badge — reschedule within the step 1 date range.
   * Also sets the first date on undated "Add new" cards.
   */
  const handleDueDateClick = useCallback((assignment) => {
    setAddToDateContext({
      type: 'assignment',
      assignment,
      mode: 'reschedule'
    });
    callback?.onDueDateClick?.(assignment);
  }, [callback]);

  const handleAddToDateModalDone = useCallback((_assignment, targetDateIso) => {
    if (!addToDateContext || !targetDateIso) {
      closeAddToDateModal();

      return;
    }

    let result;

    if (addToDateContext.mode === 'reschedule') {
      result = applyRescheduleAssignmentDate({
        assignments,
        assignmentId: addToDateContext.assignment.id,
        targetDateIso
      });
      // The card re-sorts into date order; keep it in view.
      setScrollToAssignmentId(result.targetAssignmentId || '');
    } else {
      result = applyAddAssignmentContentToDate({
        assignments,
        sourceAssignmentId: addToDateContext.assignment.id,
        targetDateIso,
        sourceTocData,
        scheduleFormValues
      });
    }

    updateAssignments(result.assignments);
    setEditingAssignmentId(result.targetAssignmentId || '');
    closeAddToDateModal();
    callback?.onAddToDate?.({
      targetDateIso,
      context: addToDateContext,
      targetAssignmentId: result.targetAssignmentId,
      assignments: result.assignments
    });
  }, [
    addToDateContext,
    assignments,
    callback,
    closeAddToDateModal,
    scheduleFormValues,
    sourceTocData,
    updateAssignments
  ]);

  const addToDateModalTextInfo = useMemo(() => ({
    ...textInfo,
    dueDateModalTitle: textInfo.addToDateModalTitle,
    dueDateModalDoneLabel: textInfo.addToDateModalDoneLabel
  }), [textInfo]);

  const scheduleDateBounds = useMemo(
    () => getScheduleDateBounds(scheduleFormValues),
    [scheduleFormValues]
  );

  const scheduleAddToDateFilter = useMemo(
    () => createScheduleAddToDateFilter(scheduleFormValues),
    [scheduleFormValues]
  );

  const scheduleAddToDateValidator = useMemo(
    () => createScheduleAddToDateValidator(
      scheduleFormValues,
      {
        errorMessages: {
          required: textInfo.startDateRequiredError,
          outOfRange: textInfo.endDateRequiredError
        }
      }
    ),
    [
      scheduleFormValues,
      textInfo.startDateRequiredError,
      textInfo.endDateRequiredError
    ]
  );

  const addToDateModalAssignment = useMemo(() => {
    if (!addToDateContext) {
      return null;
    }

    if (addToDateContext.type === 'assignment') {
      return addToDateContext.assignment;
    }

    return {
      id: ADD_TO_DATE_MODAL_ASSIGNMENT_ID,
      title: '',
      date: ''
    };
  }, [addToDateContext]);

  // Titles are bulk-generated from content right before save when this is on (step 1 checkbox
  // default), so individual slots don't show/edit a title while it's in effect.
  const autoNameAssignments = scheduleFormValues.autoNameAssignments !== false;

  const handleTreeContentProvider = useMemo(() => ({
    nodes: tocData,
    questionCounts: chapterMcqMapping || [],
    textInfo: treeTextInfo,
    restrictAssigned: true,
    variant: 'schedule',
    autoNameAssignments,
    style: {
      minHeight: 0,
      flex: 1,
      maxHeight: '100%'
    },
    onScheduleMenuAction: handleScheduleMenuAction,
    onAddToDate: scheduleTocAddToDateEnabled ? handleAddToDateFromTree : undefined
  }), [
    autoNameAssignments,
    chapterMcqMapping,
    handleAddToDateFromTree,
    handleScheduleMenuAction,
    scheduleTocAddToDateEnabled,
    tocData,
    treeTextInfo
  ]);

  const listItemConfig = useMemo(() => ({
    ...QUICK_SCHEDULE_ASSIGNMENT_LIST_ITEM_CONFIG,
    showTitle: !autoNameAssignments
  }), [autoNameAssignments]);

  const renderContentTree = () => (
    <div className={classes.contentTreePane}>
      <ChapterContentProvider value={handleTreeContentProvider}>
        <ChapterContentTree
          selectedNodes={[]}
          onToggleNode={noopToggleNode}
        />
      </ChapterContentProvider>
    </div>
  );

  const renderAssignmentSchedule = () => (
    <div className={classes.schedulePane}>
      <div className={classes.scheduleScroll}>
        <AssignmentList
          assignments={assignmentsWithWarnings}
          textInfo={textInfo}
          selectedAssignmentId={editingAssignmentId}
          listItemConfig={listItemConfig}
          onEdit={handleAssignmentEdit}
          onRemove={handleAssignmentSlotRemove}
          onDeselect={handleAssignmentDeselect}
          onContentDrop={handleContentDrop}
          onSectionRemove={handleSectionRemove}
          onTitleChange={handleAssignmentTitleChange}
          onDueDateClick={handleDueDateClick}
        />
      </div>
    </div>
  );

  return (
    <QuickScheduleReviewStepStylesRoot ref={reviewPaneRef} className={classes.reviewPane}>
      <div className={classes.contentLayout}>
        {renderContentTree()}
        {renderAssignmentSchedule()}
      </div>
      <AssignmentDueDateModal
        open={Boolean(addToDateContext)}
        assignment={addToDateModalAssignment}
        textInfo={addToDateModalTextInfo}
        minDate={scheduleDateBounds.minDate}
        maxDate={scheduleDateBounds.maxDate}
        filterDate={scheduleAddToDateFilter}
        validateDueDate={scheduleAddToDateValidator}
        returnStorageDate
        placeholderText={textInfo.datePlaceholder}
        onClose={closeAddToDateModal}
        onDone={handleAddToDateModalDone}
      />
    </QuickScheduleReviewStepStylesRoot>
  );
};

QuickScheduleReviewStep.propTypes = {
  textInfo: PropTypes.shape({
    sectionsPrefix: PropTypes.string,
    scheduleInstruction: PropTypes.string,
    dragZoneLabel: PropTypes.string,
    clickToEditLabel: PropTypes.string,
    clearDataLabel: PropTypes.string,
    removeLabel: PropTypes.string,
    assignmentNameLabel: PropTypes.string,
    assignmentNameValue: PropTypes.string,
    assignmentNamePlaceholder: PropTypes.string,
    addToDateModalTitle: PropTypes.string,
    addToDateModalDoneLabel: PropTypes.string,
    addToAssignmentModalTitle: PropTypes.string,
    addToAssignmentModalSubtitle: PropTypes.string,
    addToAssignmentModalDoneLabel: PropTypes.string,
    startDateRequiredError: PropTypes.string,
    endDateRequiredError: PropTypes.string,
    datePlaceholder: PropTypes.string,
    sectionsLabel: PropTypes.string,
    questionsLabel: PropTypes.string,
    chaptersLabel: PropTypes.string,
    sectionLabel: PropTypes.string,
    noQuestionsWarningLabel: PropTypes.string,
    autoNameAssignmentsInstruction: PropTypes.string,
    scheduleMenuConfig: PropTypes.object
  }).isRequired,
  scheduleFormValues: PropTypes.object,
  tocData: PropTypes.array.isRequired,
  chapterMcqMapping: PropTypes.array,
  assignments: PropTypes.array,
  editingAssignmentId: PropTypes.string,
  callback: PropTypes.shape({
    onContentDrop: PropTypes.func,
    onAssignmentEdit: PropTypes.func,
    onAssignmentSave: PropTypes.func,
    onAssignmentCancelEdit: PropTypes.func,
    onAssignmentRemove: PropTypes.func,
    onAssignmentSlotRemove: PropTypes.func,
    onAssignmentsChange: PropTypes.func,
    onSectionRemove: PropTypes.func,
    onScheduleMenuAction: PropTypes.func,
    onAddToDate: PropTypes.func,
    onOpenAddToAssignmentModal: PropTypes.func,
    onDueDateClick: PropTypes.func,
    onMenuAction: PropTypes.func
  })
};

QuickScheduleReviewStep.defaultProps = {
  scheduleFormValues: {},
  chapterMcqMapping: [],
  assignments: null,
  editingAssignmentId: '',
  callback: {}
};

export default QuickScheduleReviewStep;
